/**
 * CRM API-клиент (AK-49) — обёртки над общим `api` (src/lib/api.ts).
 *
 * Пути/поля — ровно по backend-контракту `/api/v1/crm/*`
 * (ветка feat/ak-49-crm-backend, PR #237).
 *
 * CSV-export — отдельный fetch (бинарный download, не JSON), переиспользуем
 * API_URL + credentials:'include' как в общем клиенте.
 */
import { api, API_URL } from '@/lib/api';
import type {
  CrmActivity,
  CrmActivityIn,
  CrmCompany,
  CrmCompanyIn,
  CrmCompanyUpdate,
  CrmContact,
  CrmContactIn,
  CrmDashboardRow,
  EnterClientOut,
} from './types';

const BASE = '/api/v1/crm';

// ---- companies ----
export const listCompanies = () => api.get<CrmCompany[]>(`${BASE}/companies`);

export const getCompany = (id: string) =>
  api.get<CrmCompany>(`${BASE}/companies/${id}`);

export const createCompany = (body: CrmCompanyIn) =>
  api.post<CrmCompany>(`${BASE}/companies`, body);

export const updateCompany = (id: string, body: CrmCompanyUpdate) =>
  api.patch<CrmCompany>(`${BASE}/companies/${id}`, body);

export const archiveCompany = (id: string) =>
  api.delete<void>(`${BASE}/companies/${id}`);

// ---- contacts ----
export const listContacts = (companyId: string) =>
  api.get<CrmContact[]>(`${BASE}/contacts?company_id=${companyId}`);

export const createContact = (body: CrmContactIn) =>
  api.post<CrmContact>(`${BASE}/contacts`, body);

// ---- activities ----
export const listActivities = (companyId: string) =>
  api.get<CrmActivity[]>(`${BASE}/activities?company_id=${companyId}`);

export const createActivity = (body: CrmActivityIn) =>
  api.post<CrmActivity>(`${BASE}/activities`, body);

// ---- dashboard ----
export const getDashboard = () =>
  api.get<CrmDashboardRow[]>(`${BASE}/dashboard`);

// ---- ленивый кабинет клиента + окно клиента ----

/**
 * Убедиться, что у карточки есть кабинет (создать, если его ещё нет).
 * Идемпотентно: карточка уже связана — просто вернётся она же.
 *
 * Вызывается по дороге к добавлению ПЕРВОГО поля, а не по кнопке «завести в
 * систему»: до первого поля клиент живёт как запись CRM и схемы не занимает.
 */
/**
 * «Завести хозяйство» — имя + отдел (кластер). Кабинет здесь НЕ создаётся:
 * он появится под первое поле (модель D). Идемпотентно — повторный вызов
 * переименовывает хозяйство или переносит его в другой отдел.
 */
export const setClientFarm = (
  id: string,
  body: { name: string; cluster_id: string | null },
) => api.post<CrmCompany>(`${BASE}/companies/${id}/farm`, body);

export const ensureClientTenant = (id: string) =>
  api.post<CrmCompany>(`${BASE}/companies/${id}/create-client`);

export const enterClient = (id: string) =>
  api.post<EnterClientOut>(`${BASE}/companies/${id}/enter-client`);

/**
 * Экспорт визитов в CSV (AC-3.4..3.8). Backend отдаёт text/csv с
 * Content-Disposition attachment. Триггерим браузерное скачивание blob'ом.
 * Возвращает true при успехе; кидает Error с текстом при не-2xx.
 */
export async function downloadVisitsCsv(
  from?: string,
  to?: string,
): Promise<void> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  const url = `${API_URL}${BASE}/visits/export.csv${qs ? `?${qs}` : ''}`;

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(detail || `Не удалось выгрузить CSV (HTTP ${resp.status})`);
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'crm-visits.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
